/*
 * Copyright © HatioLab Inc. All rights reserved.
 *
 * GLTF 애니메이션 타겟 선택 에디터.
 * All / None / Custom 모드로 started에 바인딩할 애니메이션 선택.
 * 각 애니메이션을 개별 미리보기(play/stop) 가능.
 */

import '@material/web/icon/icon.js'
import '@operato/i18n/ox-i18n.js'

import { css, html, PropertyValues, TemplateResult, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'

import { OxPropertyEditor, PropertySpec } from '@operato/property-editor'
import { ScrollbarStyles } from '@operato/styles/scrollbar-styles.js'

type AnimMode = 'all' | 'none' | 'custom'

function getMode(value: any): AnimMode {
  if (value === '*') return 'all'
  if (value === 'none') return 'none'
  if (Array.isArray(value) && value.length > 0) return 'custom'
  if (Array.isArray(value)) return 'custom' // empty array = custom with nothing
  if (!value) return 'all' // undefined = default = all
  return 'all'
}

@customElement('property-editor-gltf-play-targets')
export default class GLTFPlayTargetsEditor extends OxPropertyEditor {
  static styles = [
    ...OxPropertyEditor.styles,
    ScrollbarStyles,
    css`
      .mode-selector {
        display: flex;
        gap: 0;
        margin-bottom: 6px;
        border: 1px solid var(--md-sys-color-outline-variant, rgba(0, 0, 0, 0.15));
        border-radius: 6px;
        overflow: hidden;
      }

      .mode-btn {
        flex: 1;
        padding: 5px 0;
        font-size: 11px;
        font-weight: 500;
        text-align: center;
        cursor: pointer;
        background: var(--md-sys-color-surface-container, #f3edf7);
        color: var(--md-sys-color-on-surface, #1c1b1f);
        border: none;
        border-right: 1px solid var(--md-sys-color-outline-variant, rgba(0, 0, 0, 0.1));
        transition: all 0.15s;
      }

      .mode-btn:last-child {
        border-right: none;
      }

      .mode-btn:hover {
        background: var(--md-sys-color-surface-container-highest, #e6e0e9);
      }

      .mode-btn[active] {
        background: var(--md-sys-color-primary, #6750a4);
        color: var(--md-sys-color-on-primary, #fff);
      }

      .anim-list {
        display: flex;
        flex-direction: column;
        gap: 1px;
        max-height: 200px;
        overflow-y: auto;
        font-size: 12px;
      }

      .anim-item {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 4px 8px;
        border-radius: 4px;
        transition: background 0.1s;
      }

      .anim-item:hover {
        background: var(--md-sys-color-primary-container, rgba(103, 80, 164, 0.12));
      }

      .anim-item input[type='checkbox'] {
        margin: 0;
        accent-color: var(--md-sys-color-primary, #6750a4);
      }

      .anim-item span {
        flex: 1;
        font-size: 12px;
        color: var(--md-sys-color-on-surface, #1c1b1f);
      }

      .anim-item md-icon {
        --md-icon-size: 18px;
        cursor: pointer;
        color: var(--md-sys-color-on-surface-variant, #666);
        transition: color 0.15s;
      }

      .anim-item md-icon:hover {
        color: var(--md-sys-color-primary, #6750a4);
      }

      .anim-item md-icon[playing] {
        color: var(--md-sys-color-primary, #6750a4);
      }

      .mode-desc {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 11px;
        color: var(--md-sys-color-on-surface-variant, #666);
        padding: 4px 0;
      }

      .mini-btn {
        font-size: 10px;
        padding: 2px 8px;
        border: 1px solid var(--md-sys-color-outline-variant, rgba(0, 0, 0, 0.15));
        border-radius: 4px;
        background: var(--md-sys-color-surface-container, #f3edf7);
        color: var(--md-sys-color-on-surface, #1c1b1f);
        cursor: pointer;
      }

      .mini-btn:hover {
        background: var(--md-sys-color-surface-container-highest, #e6e0e9);
      }

      .play-toggle {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 4px 0;
        margin-bottom: 4px;
        cursor: pointer;
        font-size: 12px;
        font-weight: 500;
        color: var(--md-sys-color-on-surface, #1c1b1f);
      }

      .play-toggle input[type='checkbox'] {
        accent-color: var(--md-sys-color-primary, #6750a4);
      }

      .empty {
        color: var(--md-sys-color-on-surface-variant, #999);
        font-size: 11px;
        padding: 8px;
        text-align: center;
      }
    `
  ]

  @property({ type: String }) declare src: string | undefined

  @state() declare private _animNames: string[]
  @state() declare private _mode: AnimMode
  @state() declare private _playingAnim: string | null

  private _component: any = null
  private _lastExternalValue: any = undefined

  updated(changes: PropertyValues<this>) {
    if (changes.has('src')) {
      this.dispatchEvent(
        new CustomEvent('i-need-selected', {
          bubbles: true,
          composed: true,
          detail: {
            callback: (selected: any[]) => {
              this._component = selected?.[0]
              this._refreshAnimNames()
            }
          }
        })
      )
    }
  }

  private _refreshAnimNames() {
    if (!this._component) {
      this._animNames = []
      return
    }

    this._animNames = (this._component.animationNames as string[]) ?? []
  }

  private _pollTimer?: number

  connectedCallback() {
    super.connectedCallback()
    // GLTF 비동기 로드 후 애니메이션 목록 갱신을 위한 폴링 (최대 20회 = 20초)
    let retries = 0
    this._pollTimer = window.setInterval(() => {
      if (!this._animNames?.length) {
        this._refreshAnimNames()
      }
      if (this._animNames?.length || ++retries >= 20) {
        clearInterval(this._pollTimer)
        this._pollTimer = undefined
      }
    }, 1000)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    if (this._pollTimer) {
      clearInterval(this._pollTimer)
    }
  }

  editorTemplate(value: any, _spec: PropertySpec): TemplateResult {
    if (!this._animNames?.length) {
      this._refreshAnimNames()
    }

    if (value !== this._lastExternalValue) {
      this._lastExternalValue = value
      this._mode = getMode(value)
    }

    const mode = this._mode
    const animCount = this._animNames?.length ?? 0
    const targets: string[] = Array.isArray(this.value) ? this.value : (Array.isArray(value) ? value : [])

    const isPlaying = this._getPlayState()

    return html`
      <fieldset fullwidth>
        ${animCount > 0
          ? html`
              <label class="play-toggle">
                <input
                  type="checkbox"
                  .checked=${isPlaying}
                  @change=${(e: Event) => {
                    e.stopPropagation()
                    this._setPlay((e.target as HTMLInputElement).checked)
                  }}
                />
                <span>Play</span>
              </label>

              <div class="mode-selector">
                <div class="mode-btn" ?active=${mode === 'all'} @click=${() => this._setMode('all')}>All</div>
                <div class="mode-btn" ?active=${mode === 'none'} @click=${() => this._setMode('none')}>None</div>
                <div class="mode-btn" ?active=${mode === 'custom'} @click=${() => this._setMode('custom')}>Custom</div>
              </div>

              ${mode === 'all'
                ? html`<div class="mode-desc">${animCount} animations — play all</div>`
                : nothing}
              ${mode === 'none'
                ? html`<div class="mode-desc">No animations auto-play</div>`
                : nothing}
              ${mode === 'custom'
                ? html`
                    <div class="mode-desc">
                      <span style="flex:1">${targets.length} / ${animCount} selected</span>
                      <button class="mini-btn" @click=${() => this._applyValue([...(this._animNames ?? [])])}>All</button>
                      <button class="mini-btn" @click=${() => this._applyValue([])}>Clear</button>
                    </div>
                  `
                : nothing}

              <div class="anim-list">
                ${(this._animNames ?? []).map(
                  name => html`
                    <div class="anim-item">
                      ${mode === 'custom'
                        ? html`
                            <input
                              type="checkbox"
                              .checked=${targets.includes(name)}
                              @change=${(e: Event) => {
                                e.stopPropagation()
                                this._onToggle(name, (e.target as HTMLInputElement).checked, targets)
                              }}
                            />
                          `
                        : nothing}
                      <span>${name}</span>
                      <md-icon
                        ?playing=${this._playingAnim === name}
                        @click=${() => this._togglePreview(name)}
                        title="preview"
                      >${this._playingAnim === name ? 'stop' : 'play_arrow'}</md-icon>
                    </div>
                  `
                )}
              </div>
            `
          : html`<div class="empty">No animations available</div>`}
      </fieldset>
    `
  }

  private _setMode(mode: AnimMode) {
    const prevMode = this._mode
    this._mode = mode

    let newValue: any
    switch (mode) {
      case 'all':
        newValue = '*'
        break
      case 'none':
        newValue = 'none'
        break
      case 'custom':
        if (prevMode === 'all') {
          newValue = [...(this._animNames ?? [])]
        } else if (prevMode === 'none') {
          newValue = []
        } else {
          newValue = Array.isArray(this.value) ? this.value : []
        }
        break
    }

    this._applyValue(newValue)
  }

  private _onToggle(name: string, checked: boolean, current: string[]) {
    const list = [...current]
    if (checked) {
      if (!list.includes(name)) list.push(name)
    } else {
      const idx = list.indexOf(name)
      if (idx >= 0) list.splice(idx, 1)
    }
    this._applyValue(list)
  }

  private _applyValue(newValue: any) {
    this.value = newValue
    this.requestUpdate()

    this.dispatchEvent(
      new CustomEvent('i-need-selected', {
        bubbles: true,
        composed: true,
        detail: {
          callback: (selected: any[]) => {
            selected[0].set('playTargets', newValue)
          }
        }
      })
    )
  }

  private _getPlayState(): boolean {
    return this._component?.state?.play !== false
  }

  private _setPlay(value: boolean) {
    if (!this._component) return
    this._component.set('play', value)
    this.requestUpdate()
  }

  private _togglePreview(name: string) {
    if (!this._component) return

    const ro = this._component.realObject as any
    if (!ro?._animationActions) return

    const action = ro._animationActions.get(name)
    if (!action) return

    if (this._playingAnim === name) {
      action.stop()
      this._playingAnim = null
    } else {
      // 이전 프리뷰 중지
      if (this._playingAnim) {
        ro._animationActions.get(this._playingAnim)?.stop()
      }
      action.play()
      this._playingAnim = name
    }

    this._component.invalidate()
    this.requestUpdate()
  }
}
